import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import type { Reel } from "../lib/flyerViewer";
import { todayIso } from "../lib/format";
import { getJson } from "../lib/request";
import { withSheet, withoutSheet, type SheetKind } from "../lib/sheets";
import { AppCtxContext, type AppCtx, type TodayInfo } from "./appState";
import { FlyerViewer } from "./FlyerViewer";
import { Toast } from "./Toast";

export function AppProvider({ email, onSignOut, children }: { email: string | null; onSignOut: () => void; children: ReactNode }) {
  const [, setSp] = useSearchParams();
  const [version, setVersion] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [todayInfo, setTodayInfo] = useState<TodayInfo | null>(null);
  // The viewer sits above every page and sheet, so it is held here rather than
  // in the URL: the reel it steps through is the list the flyer was tapped in.
  const [reel, setReel] = useState<Reel | null>(null);

  useEffect(() => {
    let live = true;
    getJson<TodayInfo>("/api/shows/today", "Could not load today")
      .then((t) => live && setTodayInfo(t))
      .catch(() => live && setTodayInfo(null));
    return () => {
      live = false;
    };
  }, [version]);

  const open = useCallback((kind: SheetKind, value: string | number) => setSp((prev) => withSheet(prev, kind, value)), [setSp]);
  const close = useCallback(() => setSp((prev) => withoutSheet(prev)), [setSp]);
  const changed = useCallback((m?: string) => {
    setVersion((v) => v + 1);
    if (m) setMessage(m);
  }, []);
  const clearToast = useCallback(() => setMessage(null), []);
  const closeZoom = useCallback(() => setReel(null), []);

  const value = useMemo<AppCtx>(
    () => ({
      open,
      close,
      zoom: setReel,
      version,
      changed,
      toast: setMessage,
      today: todayInfo?.date ?? todayIso(),
      todayInfo,
      firstYear: todayInfo?.first_year ?? Number(todayIso().slice(0, 4)),
      email,
      signOut: onSignOut,
    }),
    [open, close, version, changed, todayInfo, email, onSignOut],
  );

  return (
    <AppCtxContext.Provider value={value}>
      {children}
      {reel?.shots.length ? <FlyerViewer key={reel.shots[reel.at]?.url} reel={reel} onClose={closeZoom} /> : null}
      {message && <Toast text={message} onDone={clearToast} />}
    </AppCtxContext.Provider>
  );
}
